import { useParams } from "react-router-dom";
import { useBundle, divisionStandings, bundleViewState } from "../../lib/bundle";
import { formatPct } from "../../lib/format";
import { DivisionPageHeader } from "../../components/DivisionTabs";
import { Badge, Card, EmptyState, LoadingBlock, stagger, cx } from "../../components/ui";

/** Public per-pool standings (spec §10). Live via the bundle's 30s polling. */
export function Standings() {
  const { tid, did } = useParams();
  const q = useBundle(tid);
  const bundle = q.data;

  const view = bundleViewState(q);
  if (view === "notFound")
    return <EmptyState title="Tournament not found" className="mx-auto max-w-md" />;
  if (view === "error")
    return (
      <EmptyState
        title="Couldn't load this tournament"
        hint="Check your connection and try again."
        className="mx-auto max-w-md"
      />
    );
  if (view === "loading" || !bundle) return <LoadingBlock />;
  const division = bundle.divisions.find((d) => d.id === did);
  if (!division) return <EmptyState title="Division not found" className="mx-auto max-w-md" />;

  const locked = Boolean(division.lockedSeeds);
  const pools = divisionStandings(bundle, division.id);

  return (
    <div className="space-y-8">
      <DivisionPageHeader bundle={bundle} division={division} />

      {!locked ? (
        <Badge tone="amber" pulse>
          Provisional — seeds not final
        </Badge>
      ) : null}

      {pools.length === 0 ? (
        <EmptyState title="No pools yet" hint="Standings appear once pools are set up." />
      ) : null}

      {/*
        Pools side by side, not stacked. A standings table is eight narrow
        numeric columns and one name — widen one and every W/L/T drifts away
        from the team it describes. Two pools at the width they were designed
        at also answer the question people actually have here, which is how
        their pool compares to the one next to it.

        A one-pool division opts out: the second column would have nothing to
        hold, and a lone table pinned to the left of a half-empty grid is the
        stranded row this whole change exists to avoid. It gets a reading-width
        column instead.
      */}
      <div
        className={cx("grid items-start gap-8", pools.length > 1 ? "xl:grid-cols-2" : "max-w-3xl")}
      >
        {pools.map(({ poolId, poolName, standings }, pi) => (
          <section key={poolId} className="animate-rise space-y-3" style={stagger(pi + 1)}>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
              {poolName}
            </h2>
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/70 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                      <th className="w-12 px-3 py-2.5">#</th>
                      <th className="py-2.5 pr-3">Team</th>
                      <th className="px-2 py-2.5 text-center">W</th>
                      <th className="px-2 py-2.5 text-center">L</th>
                      <th className="px-2 py-2.5 text-center">T</th>
                      <th className="px-3 py-2.5 text-right">Pct</th>
                      <th className="hidden px-2 py-2.5 text-right sm:table-cell">RS</th>
                      <th className="hidden px-3 py-2.5 text-right sm:table-cell">RA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standings.rows.map((r, i) => (
                      <tr
                        key={r.teamId}
                        className="border-b border-slate-50 transition-colors last:border-0 hover:bg-brand-50/40"
                      >
                        <td className="px-3 py-2.5">
                          <RankChip rank={i + 1} />
                        </td>
                        <td
                          className="py-2.5 pr-3 font-medium text-navy-950"
                          title={r.unresolved ? "Tiebreaker unresolved" : undefined}
                        >
                          {r.teamName}
                          {r.unresolved ? (
                            <Badge tone="amber" className="ml-2">
                              tie
                            </Badge>
                          ) : null}
                        </td>
                        <td className="px-2 text-center font-semibold tabular-nums text-navy-950">
                          {r.wins}
                        </td>
                        <td className="px-2 text-center tabular-nums text-slate-500">{r.losses}</td>
                        <td className="px-2 text-center tabular-nums text-slate-500">{r.ties}</td>
                        <td className="px-3 text-right tabular-nums text-slate-600">
                          {formatPct(r.winPct)}
                        </td>
                        <td
                          className="hidden px-2 text-right tabular-nums text-slate-500 sm:table-cell"
                          title={`avg ${r.avgCountedRunsScored?.toFixed(2) ?? "—"}`}
                        >
                          {r.runsScored}
                        </td>
                        <td
                          className="hidden px-3 text-right tabular-nums text-slate-500 sm:table-cell"
                          title={`avg ${r.avgCountedRunsAllowed?.toFixed(2) ?? "—"}`}
                        >
                          {r.runsAllowed}
                        </td>
                      </tr>
                    ))}
                    {standings.rows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-6 text-center text-sm text-slate-400">
                          No games played yet.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </Card>
          </section>
        ))}
      </div>
    </div>
  );
}

function RankChip({ rank }: { rank: number }) {
  return (
    <span
      className={cx(
        "flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold tabular-nums",
        rank === 1
          ? "bg-brand-600 text-brandFg shadow-sm shadow-brand-600/30"
          : rank <= 3
            ? "bg-brand-50 text-brandText"
            : "bg-slate-100 text-slate-500",
      )}
    >
      {rank}
    </span>
  );
}
